import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface InboxNavItemProps {
  label: string;
  path: string;
  qaId: string;
  active: boolean;
  width?: number;
  showUnseenDot: boolean;
}

/**
 * Inbox TopBar nav item: the nav `Link` wrapped in a relative container so the
 * unseen-badge dot can overlay the top-right corner without affecting the
 * measured label width or shifting the nav layout when toggled.
 */
export const InboxNavItem = ({
  label,
  path,
  qaId,
  active,
  width,
  showUnseenDot,
}: InboxNavItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Link
        {...qaAttr({ id: qaId, value: active ? 'active' : undefined })}
        to={path}
        className="hover:opacity-80 inline-block text-center"
        style={{
          transition: `all ${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.easeInOut}`,
          color: active ? theme.text.primary : theme.text.secondary,
          // If changes done here for font size, font weight or letter spacing,
          // make sure to update the canvas measurement in HomeTopBarHeader accordingly
          fontSize: responsive.font.lg,
          fontWeight: active ? responsive.fontWeight.semibold : responsive.fontWeight.regular,
          letterSpacing: active ? `${responsive.topBar.activeLetterSpacing}px` : '0px',
          width: width ? `${width}px` : 'auto',
          minWidth: width ? `${width}px` : 'auto',
        }}
      >
        {label}
      </Link>
      {showUnseenDot && (
        <span
          {...qaAttr({ id: 'topbar-inbox-unseen-dot', status: 'visible' })}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -responsive.spacing.xxs,
            right: -responsive.spacing.xs,
            width: responsive.spacing.sm,
            height: responsive.spacing.sm,
            borderRadius: responsive.borderRadius.round,
            backgroundColor: theme.activeRed,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};
